import { offline } from '../core/offline.js';
import { stripCountry } from '../core/country.js';
import { catalog } from '../core/catalog.js';
import { t } from '../core/i18n.js';
import { escapeHtml, formatBytes, formatMinutes } from '../core/utils.js';
import { pageHead, empty, img, enhance } from '../ui/render.js';
import { playOffline, openOfflineMenu, confirmRemove, progressText, errorText } from '../ui/offline-ui.js';

/* Descargas: lo guardado en este dispositivo para ver sin conexión, con su progreso. La lista se
   repinta cuando cambia algo; mientras baja, solo se actualizan la barra y el texto de su fila. */

/** En perfiles infantiles solo se ven las descargas hechas desde ese mismo perfil. */
function visibleItems() {
    const list = offline.list().filter((r) => !catalog.id || r.profileId === catalog.id);
    if (!catalog.isKids) return list;
    const me = catalog.viewer ? catalog.viewer.id : '';
    return list.filter((r) => r.viewerId === me);
}

function statusText(r) {
    if (r.status === 'done') return [formatBytes(r.size), r.durationSecs ? formatMinutes(r.durationSecs) : ''].filter(Boolean).join(' · ');
    if (r.status === 'downloading') return t('offline.downloading') + ' · ' + progressText(r) + (r.size ? ' · ' + formatBytes(r.received) + ' / ' + formatBytes(r.size) : '');
    if (r.status === 'queued') return t('offline.queued');
    if (r.status === 'error') return errorText(r) + (r.detail ? ' — ' + r.detail : '');
    return (r.waiting ? t('offline.waiting') : t('offline.paused')) + (r.received ? ' · ' + formatBytes(r.received) : '');
}

const pct = (r) => (r.size ? Math.min(100, (r.received / r.size) * 100) : 0);

function rowHtml(r) {
    const done = r.status === 'done';
    return `<div class="offline-row is-${r.status}" data-offline-row="${escapeHtml(r.key)}">
        <button type="button" class="offline-thumb" ${done ? `data-offline-play="${escapeHtml(r.key)}"` : 'disabled'} aria-label="${escapeHtml((done ? t('offline.play') + ': ' : '') + stripCountry(r.title))}">
            <span class="mi offline-thumb-fallback">${r.kind === 'vod' ? 'movie' : 'tv'}</span>
            ${img(r.poster, '')}
            ${done ? '<span class="offline-thumb-play"><span class="mi">play_arrow</span></span>' : ''}
        </button>
        <div class="offline-body">
            <div class="offline-title">${escapeHtml(stripCountry(r.title))}</div>
            ${r.subtitle ? `<div class="offline-sub">${escapeHtml(r.subtitle)}</div>` : ''}
            <div class="offline-status" data-offline-status>${escapeHtml(statusText(r))}</div>
            ${done ? '' : `<div class="offline-bar ${r.size ? '' : 'is-unknown'}"><i data-offline-bar style="width:${pct(r)}%"></i></div>`}
        </div>
        <div class="offline-actions">
            ${r.status === 'downloading' || r.status === 'queued' ? `<button type="button" class="btn btn-icon" data-offline-pause="${escapeHtml(r.key)}" aria-label="${t('offline.pause')}"><span class="mi">pause</span></button>` : ''}
            ${r.status === 'paused' || r.status === 'error' ? `<button type="button" class="btn btn-icon" data-offline-resume="${escapeHtml(r.key)}" aria-label="${t(r.status === 'error' ? 'offline.retry' : 'offline.resume')}"><span class="mi">${r.status === 'error' ? 'refresh' : 'play_arrow'}</span></button>` : ''}
            ${done ? `<button type="button" class="btn btn-icon" data-offline-more="${escapeHtml(r.key)}" aria-label="${t('common.moreOptions')}"><span class="mi">more_vert</span></button>` : `<button type="button" class="btn btn-icon" data-offline-remove="${escapeHtml(r.key)}" aria-label="${t('offline.cancel')}"><span class="mi">close</span></button>`}
        </div>
    </div>`;
}

export default {
    path: '/offline/',
    nav: 'offline',

    render() {
        return `<div class="page" data-name="offline">
            <div class="page-content">
                ${pageHead({ title: t('offline.title') })}
                <div class="page-sub" data-usage></div>
                <div class="offline-list" data-list></div>
            </div>
        </div>`;
    },

    mount(page) {
        const root = page.el;
        const list = root.querySelector('[data-list]');
        const usageEl = root.querySelector('[data-usage]');
        let alive = true;

        const paintUsage = async () => {
            if (!offline.supported) return;
            const u = await offline.usage();
            if (!alive) return;
            usageEl.textContent = u.free != null ? t('offline.usage', { used: formatBytes(u.used), free: formatBytes(u.free) }) : t('offline.usageUsed', { used: formatBytes(u.used) });
        };

        const paint = () => {
            if (!offline.supported) {
                usageEl.textContent = '';
                list.innerHTML = empty({ icon: 'download', title: t('offline.unsupportedTitle'), text: t('offline.unsupportedText') });
                return;
            }
            const items = visibleItems();
            const active = items.some((r) => r.status === 'downloading' || r.status === 'queued');
            list.innerHTML = items.length
                ? (active ? `<div class="offline-note"><span class="mi">info</span>${t('offline.keepOpen')}</div>` : '') + items.map(rowHtml).join('')
                : empty({ icon: 'download', title: t('offline.emptyTitle'), text: t('offline.emptyText') });
            enhance(list);
            paintUsage();
        };

        const onProgress = (rec) => {
            const row = list.querySelector(`[data-offline-row="${CSS.escape(rec.key)}"]`);
            if (!row) return;
            const bar = row.querySelector('[data-offline-bar]');
            if (bar) bar.style.width = pct(rec) + '%';
            const status = row.querySelector('[data-offline-status]');
            if (status) status.textContent = statusText(rec);
        };

        root.addEventListener('click', (e) => {
            const hit = (attr) => {
                const el = e.target.closest('[' + attr + ']');
                return el ? offline.get(el.getAttribute(attr)) : null;
            };
            let rec;
            if ((rec = hit('data-offline-play'))) return playOffline(rec);
            if ((rec = hit('data-offline-pause'))) return offline.pause(rec.key);
            if ((rec = hit('data-offline-resume'))) return offline.resume(rec.key);
            if ((rec = hit('data-offline-remove'))) return confirmRemove(rec);
            if ((rec = hit('data-offline-more'))) return openOfflineMenu(rec, { showList: false });
            return null;
        });

        paint();
        const off = [offline.on('change', paint), offline.on('progress', onProgress), catalog.on('change', paint)];
        if (window.IPTV.device.isTV) setTimeout(() => window.IPTV.tvnav.focusLayer(root), 100);
        return () => {
            alive = false;
            off.forEach((fn) => fn());
        };
    }
};
